import { useMemo, useState } from 'react';
import {
  useCatalogue,
  useCreateSubscription,
  useEndSubscription,
  useTenantExtensions,
  useTenantSubscriptions,
} from '../../hooks';
import type {
  ICatalogueItem,
  ISubscription,
  ISubscriptionInput,
  ITenantSummary,
} from '../../types';
import {
  PRICING_SHORT,
  addDays,
  lineState,
  money,
  monthlyValue,
  quantityFor,
  type LineState,
} from '../Catalogue/pricing';
import { TenantBillingSettings } from './TenantBillingSettings';
import styles from './TenantBilling.module.scss';

const today = () => new Date().toISOString().slice(0, 10);

const STATE_LABEL: Record<LineState, string> = {
  live: 'Live',
  upcoming: 'Upcoming',
  ended: 'Ended',
};

const formatDate = (iso: string | null): string =>
  iso ? new Date(`${iso}T00:00:00Z`).toLocaleDateString('en-GB') : 'open';

interface AddPanelProps {
  tenant: ITenantSummary;
  catalogue: ICatalogueItem[];
  onClose: () => void;
  onSubmit: (lines: Parameters<ReturnType<typeof useCreateSubscription>['mutate']>[0][]) => void;
  saving: boolean;
}

const AddPanel = ({ tenant, catalogue, onClose, onSubmit, saving }: AddPanelProps) => {
  const sellable = catalogue.filter((item) => item.active && item.default_price !== null);
  const [code, setCode] = useState(sellable[0]?.code ?? '');
  const item = sellable.find((entry) => entry.code === code) ?? null;
  const [price, setPrice] = useState<string>(item?.default_price?.toString() ?? '');
  const [startsOn, setStartsOn] = useState(today());
  const [endsOn, setEndsOn] = useState('');
  const [trialDays, setTrialDays] = useState<string>('');
  const [reason, setReason] = useState('');

  const pick = (nextCode: string) => {
    setCode(nextCode);
    const next = sellable.find((entry) => entry.code === nextCode);
    setPrice(next?.default_price?.toString() ?? '');
  };

  if (!item) {
    return (
      <aside className={styles.panel}>
        <p className={styles.muted}>
          Nothing in the catalogue is priced and available yet. Set a price in Catalogue first.
        </p>
        <button type="button" className={styles.ghost} onClick={onClose}>
          Close
        </button>
      </aside>
    );
  }

  const quantity = quantityFor(item, tenant.venues, tenant.users);
  const period: ISubscriptionInput['period'] =
    item.pricing_model === 'one_off' ? 'one_off' : 'monthly';
  const numericPrice = Number(price) || 0;
  const trial = Number(trialDays) || 0;
  const paidStart = trial > 0 ? addDays(startsOn, trial) : startsOn;
  const monthly = period === 'one_off' ? 0 : numericPrice * (quantity ?? 1);

  const submit = (event: React.FormEvent) => {
    event.preventDefault();
    const lines: ISubscriptionInput[] = [];
    if (trial > 0) {
      lines.push({
        product_code: item.code,
        price: 0,
        period,
        quantity,
        starts_on: startsOn,
        ends_on: addDays(startsOn, trial - 1),
        reason: `Free trial, ${trial} days`,
      });
    }
    lines.push({
      product_code: item.code,
      price: numericPrice,
      period,
      quantity,
      starts_on: paidStart,
      ends_on: endsOn || null,
      reason: reason || null,
    });
    onSubmit(lines);
  };

  return (
    <aside className={styles.panel} aria-label="Add product">
      <header className={styles.panelHeader}>
        <h2 className={styles.panelTitle}>Add product</h2>
        <button type="button" className={styles.close} onClick={onClose} aria-label="Close">
          ×
        </button>
      </header>
      <form className={styles.form} onSubmit={submit}>
        <label className={styles.field}>
          <span>Product</span>
          <select value={code} onChange={(event) => pick(event.target.value)}>
            {sellable.map((entry) => (
              <option key={entry.code} value={entry.code}>
                {entry.name}
              </option>
            ))}
          </select>
          <small>
            {item.description ??
              `Default ${money(item.default_price)} ${PRICING_SHORT[item.pricing_model]}`}
          </small>
        </label>
        <label className={styles.field}>
          <span>Price for this tenant (£ {PRICING_SHORT[item.pricing_model]})</span>
          <input
            type="number"
            min={0}
            step="0.01"
            value={price}
            onChange={(event) => setPrice(event.target.value)}
            required
          />
          {quantity !== null ? (
            <small>
              × {quantity.toLocaleString('en-GB')}{' '}
              {item.pricing_model === 'per_venue' ? 'venues' : 'users'} today = {money(monthly)} /
              month
            </small>
          ) : null}
        </label>
        <div className={styles.row}>
          <label className={styles.field}>
            <span>Starts</span>
            <input
              type="date"
              value={startsOn}
              onChange={(event) => setStartsOn(event.target.value)}
              required
            />
          </label>
          <label className={styles.field}>
            <span>Ends (optional)</span>
            <input
              type="date"
              value={endsOn}
              min={paidStart}
              onChange={(event) => setEndsOn(event.target.value)}
            />
          </label>
        </div>
        {period !== 'one_off' ? (
          <label className={styles.field}>
            <span>Free trial first (days)</span>
            <input
              type="number"
              min={0}
              step={1}
              value={trialDays}
              placeholder="0"
              onChange={(event) => setTrialDays(event.target.value)}
            />
            {trial > 0 ? (
              <small>
                Free from {formatDate(startsOn)} to {formatDate(addDays(startsOn, trial - 1))}, then{' '}
                {money(numericPrice)} {PRICING_SHORT[item.pricing_model]} from{' '}
                {formatDate(paidStart)}.
              </small>
            ) : null}
          </label>
        ) : null}
        <label className={styles.field}>
          <span>Note (optional)</span>
          <input
            value={reason}
            onChange={(event) => setReason(event.target.value)}
            placeholder="e.g. agreed with Dan, renewal in April"
          />
        </label>
        <div className={styles.actions}>
          <button type="button" className={styles.ghost} onClick={onClose}>
            Cancel
          </button>
          <button type="submit" className={styles.primary} disabled={saving || !price}>
            {saving ? 'Saving…' : trial > 0 ? 'Add trial and subscription' : 'Add subscription'}
          </button>
        </div>
      </form>
    </aside>
  );
};

export const TenantBilling = ({ tenant }: { tenant: ITenantSummary }) => {
  const { data: subs = [], isLoading, error } = useTenantSubscriptions(tenant.name);
  const { data: catalogue = [] } = useCatalogue();
  const { data: extensions = [] } = useTenantExtensions(tenant.name);
  const create = useCreateSubscription(tenant.name);
  const end = useEndSubscription(tenant.name);
  const [adding, setAdding] = useState(false);

  const byCode = useMemo(() => new Map(catalogue.map((item) => [item.code, item])), [catalogue]);
  const grouped = useMemo(() => {
    const groups: Record<LineState, ISubscription[]> = { live: [], upcoming: [], ended: [] };
    subs.forEach((sub) => groups[lineState(sub)].push(sub));
    return groups;
  }, [subs]);
  const mrr = grouped.live.reduce((total, sub) => total + monthlyValue(sub), 0);

  /** Installed add-ons with no live subscription, and live add-on subscriptions with nothing installed. */
  const mismatches = useMemo(() => {
    const installed = new Set(
      extensions
        .map((extension) => extension.addon_code)
        .filter((code): code is string => Boolean(code)),
    );
    const billedAddons = new Set(
      grouped.live
        .map((sub) => byCode.get(sub.product_code)?.addon_code ?? null)
        .filter((code): code is string => Boolean(code)),
    );
    const unbilled = Array.from(installed)
      .filter((code) => !billedAddons.has(code))
      .map((code) => ({
        code,
        item: catalogue.find((entry) => entry.addon_code === code) ?? null,
      }));
    const uninstalled = Array.from(billedAddons)
      .filter((code) => !installed.has(code))
      .map((code) => ({
        code,
        item: catalogue.find((entry) => entry.addon_code === code) ?? null,
      }));
    return { unbilled, uninstalled };
  }, [extensions, grouped.live, byCode, catalogue]);

  const submitLines = async (lines: Parameters<typeof create.mutate>[0][]) => {
    for (const line of lines) {
      // eslint-disable-next-line no-await-in-loop
      await create.mutateAsync(line);
    }
    setAdding(false);
  };

  if (isLoading) return <p className={styles.muted}>Loading subscriptions…</p>;
  if (error)
    return (
      <p className={styles.error}>
        Could not load: {(error as { message?: string }).message ?? 'unknown error'}
      </p>
    );

  return (
    <div className={adding ? styles.split : undefined}>
      <div className={styles.main}>
        <TenantBillingSettings tenant={tenant} />
        <div className={styles.summaryRow}>
          <div className={styles.stat}>
            <span className={styles.statLabel}>Monthly recurring</span>
            <span className={styles.statValue}>{money(Math.round(mrr * 100) / 100)}</span>
            <span className={styles.statHint}>{grouped.live.length} live lines</span>
          </div>
          <button type="button" className={styles.primary} onClick={() => setAdding(true)}>
            + Add product
          </button>
        </div>
        {mismatches.unbilled.length > 0 || mismatches.uninstalled.length > 0 ? (
          <section className={styles.warnings} aria-label="Billing warnings">
            {mismatches.unbilled.map(({ code, item }) => (
              <div key={`unbilled-${code}`} className={styles.warning} data-kind="unbilled">
                <span className={styles.warningTitle}>Installed but not billed</span>
                <span className={styles.warningText}>
                  {item?.name ?? code} is connected to a brand but has no live subscription.
                  {item && item.default_price !== null && item.active
                    ? ' Add it below.'
                    : ' It has no price in the catalogue yet.'}
                </span>
              </div>
            ))}
            {mismatches.uninstalled.map(({ code, item }) => (
              <div key={`uninstalled-${code}`} className={styles.warning} data-kind="uninstalled">
                <span className={styles.warningTitle}>Billed but not installed</span>
                <span className={styles.warningText}>
                  {item?.name ?? code} has a live subscription but is not connected to any brand.
                </span>
              </div>
            ))}
          </section>
        ) : null}
        {(['live', 'upcoming', 'ended'] as LineState[]).map((state) =>
          grouped[state].length === 0 && state !== 'live' ? null : (
            <section key={state} className={styles.group}>
              <h3 className={styles.groupTitle} data-state={state}>
                {STATE_LABEL[state]}
              </h3>
              {grouped[state].length === 0 ? (
                <p className={styles.muted}>
                  No live subscriptions. This tenant is not being billed.
                </p>
              ) : null}
              <ul className={styles.lines}>
                {grouped[state].map((sub) => {
                  const item = byCode.get(sub.product_code);
                  return (
                    <li key={sub.id} className={styles.line} data-state={state}>
                      <div className={styles.lineMain}>
                        <span className={styles.lineName}>{item?.name ?? sub.product_code}</span>
                        <span className={styles.lineMeta}>
                          {sub.price === 0
                            ? 'Free'
                            : `${money(sub.price)} ${
                                item ? PRICING_SHORT[item.pricing_model] : `/ ${sub.period}`
                              }`}
                          {sub.quantity !== null
                            ? ` × ${sub.quantity.toLocaleString('en-GB')}`
                            : ''}
                          {' · '}
                          {formatDate(sub.starts_on)} → {formatDate(sub.ends_on)}
                          {sub.reason ? ` · ${sub.reason}` : ''}
                        </span>
                      </div>
                      <div className={styles.lineRight}>
                        <span className={styles.lineValue}>
                          {sub.period === 'one_off'
                            ? `${money(sub.price)} once`
                            : `${money(monthlyValue(sub))} / mo`}
                        </span>
                        {state !== 'ended' && sub.ends_on === null ? (
                          <button
                            type="button"
                            className={styles.endButton}
                            disabled={end.isLoading}
                            onClick={() =>
                              end.mutate({
                                id: sub.id,
                                endsOn: today(),
                                reason: 'Ended from Nexus',
                              })
                            }
                          >
                            End today
                          </button>
                        ) : null}
                      </div>
                    </li>
                  );
                })}
              </ul>
            </section>
          ),
        )}
      </div>
      {adding ? (
        <AddPanel
          tenant={tenant}
          catalogue={catalogue}
          onClose={() => setAdding(false)}
          onSubmit={submitLines}
          saving={create.isLoading}
        />
      ) : null}
    </div>
  );
};
